import type { User } from '@iheartjane/types';
import { Storage } from '@iheartjane/utils';
import type { Reducer } from '@reduxjs/toolkit';
import { createAction } from '@reduxjs/toolkit';
import cloneDeep from 'lodash-es/cloneDeep';
import type { DeepReadonly } from 'ts-essentials';

import { APPLICATION_CLOSE_MODAL } from '../../customer/redux/application';
import { NotificationsService } from '../../services/notifications';
import { UsersSource } from '../../sources/users';
import type { CustomerAction, CustomerThunkAction } from '../customerAction';
import type { AppState } from '../redux';
import { CUSTOMER_LOG_OUT } from './customer';

const USER_SESSION_STORAGE_KEY = 'user_session';

export const SET_PREVIOUS_PATH = 'users/set-previous-path';

export const setPreviousPath = createAction<string, typeof SET_PREVIOUS_PATH>(
  SET_PREVIOUS_PATH
);

export const UPDATE_USERS = 'users/update-users';
const updateUsersSuccess = createAction<User, typeof UPDATE_USERS>(
  UPDATE_USERS
);

export const updateUsers = ({
  attributes,
  disableNotification = false,
  onSuccess,
  onError,
}: {
  attributes: Record<string, string | boolean | undefined>;
  disableNotification?: boolean;
  onError?: (validations: Record<string, string[]>) => void;
  onSuccess?: () => void;
}): CustomerThunkAction => {
  const updates = cloneDeep(attributes);

  if (updates['phone'] && typeof updates['phone'] === 'string') {
    updates['phone'] = updates['phone'].replace(/-/g, '');
  }

  return (dispatch) => {
    UsersSource.update({
      user: { ...(({ promotions_opt_out: _, ...rest }) => rest)(updates) },
      promotions_opt_out: updates['promotions_opt_out'] as boolean,
    }).then((result) => {
      const { user, validations } = result;

      if (!validations) {
        !disableNotification &&
          NotificationsService.success('Profile has been updated');
        Storage.set(USER_SESSION_STORAGE_KEY, user);
        onSuccess && onSuccess();
        dispatch(updateUsersSuccess(user));
      } else {
        onError && onError(validations);
      }
    });
  };
};

export type UsersActions =
  | ReturnType<typeof setPreviousPath>
  | ReturnType<typeof updateUsersSuccess>;

export type UsersState = DeepReadonly<{
  hasLoadedReservations: boolean;
  isCancellingReservation: boolean;
  isLoading: boolean;
  isLoadingReservations: boolean;
  previousPath: string;
  productsOrdered: number;
  reviewsLeft: number;
  shouldUpdate: boolean;
  user:
    | {
        birth_date: string;
        email: string;
        nickname: string;
        password: string;
        phone: string;
        promotions_opt_out: boolean;
      }
    | User;
}>;

const getInitialState = (): UsersState => ({
  user: {
    birth_date: '',
    nickname: '',
    email: '',
    phone: '',
    password: '',
    promotions_opt_out: false,
  },
  productsOrdered: 0,
  reviewsLeft: 0,
  shouldUpdate: true,
  hasLoadedReservations: false,
  isCancellingReservation: false,
  isLoadingReservations: false,
  isLoading: true,
  previousPath: '',
});

export const usersReducer: Reducer<UsersState, CustomerAction> = (
  state = getInitialState(),
  action
) => {
  switch (action.type) {
    case UPDATE_USERS:
      return { ...state, user: { ...state.user, ...action.payload } };

    case APPLICATION_CLOSE_MODAL:
      return { ...state, user: getInitialState().user };

    case CUSTOMER_LOG_OUT:
      return {
        ...state,
        user: getInitialState().user,
        hasLoadedReservations: false,
      };

    case SET_PREVIOUS_PATH:
      return { ...state, previousPath: action.payload };
  }

  return state;
};

export const selectUsers = (state: AppState) => state.users;
